import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  SourcingPurchaseBulkCreateRequest,
  SourcingService,
} from 'api-clients/monolith';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useToaster } from '@/components/base/toaster';
import { useBudgetType } from '@/hooks/use-budget-type';

import { JobPostingForm } from './types';
import { useValidationSchema } from './validation/use-validation-schema';

interface UseJobPostingFormProps {
  clearJobPostingStorage: () => void;
}

export function useJobPostingForm({
  clearJobPostingStorage,
}: UseJobPostingFormProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifySuccess, notifyError } = useToaster();
  const validationSchema = useValidationSchema();
  const { calculateTotalBudget } = useBudgetType();
  const hookForm = useForm<JobPostingForm>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { getValues, handleSubmit } = hookForm;

  /* Post job by calling the bulk create API */
  const { mutate: postJob, isPending: isSubmitting } = useMutation({
    mutationFn: (requestBody: SourcingPurchaseBulkCreateRequest) =>
      SourcingService.postInternalApiSourcingSourcingPurchasesBulkCreate(
        requestBody,
      ),
    onSuccess: data => {
      const { total, created, failed } = data.summary;
      if (created === total && failed === 0) {
        notifySuccess({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{total, plural, one {Job posting has been created successfully} other {All {total} job postings have been created successfully}}',
              description: 'Success message for successful job posting',
            },
            { total },
          ),
        });
      } else {
        notifyError({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{total, plural, one {Failed to create job posting} other {Among {total} jobs, {created} jobs were posted successfully and {failed} jobs failed to be posted}}',
              description: 'Error message for job posting creation',
            },
            { total, created, failed },
          ),
        });
      }

      clearJobPostingStorage();

      const dashboardPath = location.pathname.replace(
        '/post-job',
        '/dashboard',
      );
      navigate(dashboardPath); // TODO: Navigating to dashboard instead of success page for now.
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to create job posting',
          description: 'Error message for job posting creation',
        }),
      });
    },
  });

  const handlePostJob = handleSubmit(() => {
    const formData = getValues();

    const sourcingPurchases = Object.entries(formData.channels).flatMap(
      ([channelSlug, channelData]) => {
        return Object.entries(channelData.openingBudgets)
          .filter(([_, openingBudget]) => openingBudget !== null)
          .map(([openingId, openingBudget]) => ({
            funnel_external_id: openingId,
            sourcing_channel_slug: channelSlug,
            budget: openingBudget ?? 0, // We have budget validation in the form, so we can safely assume it's not null
            total_budget: calculateTotalBudget({
              budgetType: channelData.budgetType,
              budgetPerUnit: openingBudget ?? 0,
              startDate: channelData.startsAt,
              endDate: channelData.endAt,
            }),
            starts_at: channelData.startsAt,
            end_at: channelData.endAt,
            posting_title: formData.jobDetails[openingId].title,
            budget_type: channelData.budgetType,
            end_at_type: channelData.endAtType,
          }));
      },
    );

    const requestBody: SourcingPurchaseBulkCreateRequest = {
      sourcing_purchases: sourcingPurchases,
    };

    postJob(requestBody);
  });

  return { hookForm, isSubmitting, handlePostJob };
}
